import { z } from "zod";

import {
  normalizeEmphasisRules,
  type EmphasisRules,
} from "./text-emphasis.ts";

export type { EmphasisRules } from "./text-emphasis.ts";

export type TemplateAuthority = "generic" | "journal" | "school";
export type TemplateStatus = "baseline" | "draft" | "verified";
export type ParagraphAlignment = "left" | "center" | "right" | "justify";
export type ParagraphStyleRule = {
  lineSpacing: number;
  firstLineIndentMm: number;
  spaceBeforePt: number;
  spaceAfterPt: number;
  alignment: ParagraphAlignment;
};

export type ManuscriptTemplate = {
  schemaVersion: 1;
  id: string;
  name: string;
  description: string;
  authority: TemplateAuthority;
  status: TemplateStatus;
  sourceUrl: string;
  verifiedOn: string;
  page: {
    size: "A4" | "Letter";
    marginsMm: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
    pageNumbers: boolean;
    lineNumbers: boolean;
    lineNumberMode: "continuous" | "restartPage" | "restartSection";
    lineNumberStart: number;
    lineNumberInterval: number;
  };
  typography: {
    latinFont: string;
    eastAsiaFont: string;
    bodySizePt: number;
    titleSizePt: number;
    h1SizePt: number;
    h2SizePt: number;
    h3SizePt: number;
    captionSizePt: number;
    figureCaptionSizePt: number;
    textColor: "#000000";
  };
  paragraph: {
    lineSpacing: number;
    firstLineIndentMm: number;
    spaceBeforePt: number;
    spaceAfterPt: number;
    alignment: "left" | "justify";
  };
  blockStyles: {
    abstract: ParagraphStyleRule;
    h1: ParagraphStyleRule;
    h2: ParagraphStyleRule;
    h3: ParagraphStyleRule;
    figureCaption: ParagraphStyleRule;
  };
  references: {
    numbering: "preserve" | "numeric";
    hangingIndentMm: number;
    lineSpacing: number;
    spaceAfterPt: number;
  };
  emphasisRules: EmphasisRules;
  numbering: "none" | "decimal";
};

export type TemplateCandidate = Partial<
  Omit<
    ManuscriptTemplate,
    | "page"
    | "typography"
    | "paragraph"
    | "blockStyles"
    | "references"
    | "emphasisRules"
  >
> & {
  page?: Partial<Omit<ManuscriptTemplate["page"], "marginsMm">> & {
    marginsMm?: Partial<ManuscriptTemplate["page"]["marginsMm"]>;
  };
  typography?: Partial<
    Omit<ManuscriptTemplate["typography"], "textColor">
  > & {
    textColor?: string;
  };
  paragraph?: Partial<ManuscriptTemplate["paragraph"]>;
  blockStyles?: {
    abstract?: Partial<ParagraphStyleRule>;
    h1?: Partial<ParagraphStyleRule>;
    h2?: Partial<ParagraphStyleRule>;
    h3?: Partial<ParagraphStyleRule>;
    figureCaption?: Partial<ParagraphStyleRule>;
  };
  references?: Partial<ManuscriptTemplate["references"]>;
  emphasisRules?: Partial<EmphasisRules>;
};

export const conservativeSciTemplate: ManuscriptTemplate = {
  schemaVersion: 1,
  id: "sci-conservative",
  name: "SCI 保守通用稿",
  description:
    "未指定期刊时的单栏送审基线：双倍行距、页码和连续行号；不是任何期刊的官方模板。",
  authority: "generic",
  status: "baseline",
  sourceUrl: "",
  verifiedOn: "",
  page: {
    size: "A4",
    marginsMm: {
      top: 25.4,
      right: 25.4,
      bottom: 25.4,
      left: 25.4,
    },
    pageNumbers: true,
    lineNumbers: true,
    lineNumberMode: "continuous",
    lineNumberStart: 1,
    lineNumberInterval: 1,
  },
  typography: {
    latinFont: "Times New Roman",
    eastAsiaFont: "SimSun",
    bodySizePt: 12,
    titleSizePt: 18,
    h1SizePt: 14,
    h2SizePt: 12,
    h3SizePt: 11,
    captionSizePt: 10,
    figureCaptionSizePt: 10,
    textColor: "#000000",
  },
  paragraph: {
    lineSpacing: 2,
    firstLineIndentMm: 0,
    spaceBeforePt: 0,
    spaceAfterPt: 0,
    alignment: "left",
  },
  blockStyles: {
    abstract: {
      lineSpacing: 2,
      firstLineIndentMm: 0,
      spaceBeforePt: 0,
      spaceAfterPt: 0,
      alignment: "left",
    },
    h1: {
      lineSpacing: 1,
      firstLineIndentMm: 0,
      spaceBeforePt: 12,
      spaceAfterPt: 5,
      alignment: "left",
    },
    h2: {
      lineSpacing: 1,
      firstLineIndentMm: 0,
      spaceBeforePt: 8,
      spaceAfterPt: 5,
      alignment: "left",
    },
    h3: {
      lineSpacing: 1,
      firstLineIndentMm: 0,
      spaceBeforePt: 8,
      spaceAfterPt: 5,
      alignment: "left",
    },
    figureCaption: {
      lineSpacing: 1,
      firstLineIndentMm: 0,
      spaceBeforePt: 0,
      spaceAfterPt: 6,
      alignment: "left",
    },
  },
  references: {
    numbering: "preserve",
    hangingIndentMm: 6.35,
    lineSpacing: 1,
    spaceAfterPt: 0,
  },
  emphasisRules: {
    bold: [],
    italic: [],
    boldItalic: [],
  },
  numbering: "none",
};

export const builtInTemplates: ManuscriptTemplate[] = [
  conservativeSciTemplate,
];

const paragraphStyleRuleSchema = z.object({
  lineSpacing: z.number().finite().min(1).max(3),
  firstLineIndentMm: z.number().finite().min(0).max(20),
  spaceBeforePt: z.number().finite().min(0).max(36),
  spaceAfterPt: z.number().finite().min(0).max(36),
  alignment: z.enum(["left", "center", "right", "justify"]),
});

const templateSchema = z.object({
  schemaVersion: z.literal(1),
  id: z
    .string()
    .min(1)
    .max(160)
    .regex(/^[A-Za-z0-9._-]+$/),
  name: z.string().trim().min(1).max(500),
  description: z.string().max(3000),
  authority: z.enum(["generic", "journal", "school"]),
  status: z.enum(["baseline", "draft", "verified"]),
  sourceUrl: z.string().max(3000),
  verifiedOn: z.string().max(100),
  page: z.object({
    size: z.enum(["A4", "Letter"]),
    marginsMm: z.object({
      top: z.number().finite().min(5).max(60),
      right: z.number().finite().min(5).max(60),
      bottom: z.number().finite().min(5).max(60),
      left: z.number().finite().min(5).max(60),
    }),
    pageNumbers: z.boolean(),
    lineNumbers: z.boolean(),
    lineNumberMode: z
      .enum(["continuous", "restartPage", "restartSection"])
      .default("continuous"),
    lineNumberStart: z.number().int().min(1).max(9999).default(1),
    lineNumberInterval: z.number().int().min(1).max(20).default(1),
  }),
  typography: z.object({
    latinFont: z.string().trim().min(1).max(120),
    eastAsiaFont: z.string().trim().min(1).max(120),
    bodySizePt: z.number().finite().min(8).max(18),
    titleSizePt: z.number().finite().min(12).max(32),
    h1SizePt: z.number().finite().min(9).max(24),
    h2SizePt: z.number().finite().min(8).max(22),
    h3SizePt: z.number().finite().min(8).max(20),
    captionSizePt: z.number().finite().min(8).max(16),
    figureCaptionSizePt: z.number().finite().min(8).max(16).default(10),
    textColor: z.string(),
  }),
  paragraph: z.object({
    lineSpacing: z.number().finite().min(1).max(3),
    firstLineIndentMm: z.number().finite().min(0).max(20),
    spaceBeforePt: z.number().finite().min(0).max(36).default(0),
    spaceAfterPt: z.number().finite().min(0).max(36),
    alignment: z.enum(["left", "justify"]),
  }),
  blockStyles: z
    .object({
      abstract: paragraphStyleRuleSchema,
      h1: paragraphStyleRuleSchema,
      h2: paragraphStyleRuleSchema,
      h3: paragraphStyleRuleSchema,
      figureCaption: paragraphStyleRuleSchema.default({
        lineSpacing: 1,
        firstLineIndentMm: 0,
        spaceBeforePt: 0,
        spaceAfterPt: 6,
        alignment: "left",
      }),
    })
    .default(conservativeSciTemplate.blockStyles),
  references: z
    .object({
      numbering: z.enum(["preserve", "numeric"]),
      hangingIndentMm: z.number().finite().min(0).max(20),
      lineSpacing: z.number().finite().min(1).max(3),
      spaceAfterPt: z.number().finite().min(0).max(36),
    })
    .default({
      numbering: "preserve",
      hangingIndentMm: 6.35,
      lineSpacing: 1,
      spaceAfterPt: 0,
    }),
  emphasisRules: z
    .object({
      bold: z.array(z.string().trim().min(1).max(200)).max(200).default([]),
      italic: z.array(z.string().trim().min(1).max(200)).max(200).default([]),
      boldItalic: z
        .array(z.string().trim().min(1).max(200))
        .max(200)
        .default([]),
    })
    .default({ bold: [], italic: [], boldItalic: [] }),
  numbering: z.enum(["none", "decimal"]),
});

function finiteNumber(value: unknown, fallback: number): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value: unknown, minimum: number, maximum: number, fallback: number) {
  return Math.min(maximum, Math.max(minimum, finiteNumber(value, fallback)));
}

export function normalizeTemplate(
  candidate: TemplateCandidate,
): ManuscriptTemplate {
  const fallback = conservativeSciTemplate;
  const cleanFont = (value: unknown, defaultValue: string) => {
    const text = String(value ?? "").trim();
    return text || defaultValue;
  };

  const authority = ["generic", "journal", "school"].includes(
    String(candidate.authority),
  )
    ? (candidate.authority as TemplateAuthority)
    : fallback.authority;
  const status = ["baseline", "draft", "verified"].includes(
    String(candidate.status),
  )
    ? (candidate.status as TemplateStatus)
    : fallback.status;

  return {
    schemaVersion: 1,
    id: String(candidate.id || fallback.id),
    name: String(candidate.name || "未命名模板"),
    description: String(candidate.description || ""),
    authority,
    status,
    sourceUrl: String(candidate.sourceUrl || ""),
    verifiedOn: String(candidate.verifiedOn || ""),
    page: {
      size:
        candidate.page?.size === "Letter" || candidate.page?.size === "A4"
          ? candidate.page.size
          : fallback.page.size,
      marginsMm: {
        top: clamp(candidate.page?.marginsMm?.top, 5, 60, 25.4),
        right: clamp(candidate.page?.marginsMm?.right, 5, 60, 25.4),
        bottom: clamp(candidate.page?.marginsMm?.bottom, 5, 60, 25.4),
        left: clamp(candidate.page?.marginsMm?.left, 5, 60, 25.4),
      },
      pageNumbers:
        typeof candidate.page?.pageNumbers === "boolean"
          ? candidate.page.pageNumbers
          : fallback.page.pageNumbers,
      lineNumbers:
        typeof candidate.page?.lineNumbers === "boolean"
          ? candidate.page.lineNumbers
          : fallback.page.lineNumbers,
      lineNumberMode:
        candidate.page?.lineNumberMode === "restartPage" ||
        candidate.page?.lineNumberMode === "restartSection" ||
        candidate.page?.lineNumberMode === "continuous"
          ? candidate.page.lineNumberMode
          : fallback.page.lineNumberMode,
      lineNumberStart: Math.round(
        clamp(candidate.page?.lineNumberStart, 1, 9999, 1),
      ),
      lineNumberInterval: Math.round(
        clamp(candidate.page?.lineNumberInterval, 1, 20, 1),
      ),
    },
    typography: {
      latinFont: cleanFont(
        candidate.typography?.latinFont,
        fallback.typography.latinFont,
      ),
      eastAsiaFont: cleanFont(
        candidate.typography?.eastAsiaFont,
        fallback.typography.eastAsiaFont,
      ),
      bodySizePt: clamp(candidate.typography?.bodySizePt, 8, 18, 12),
      titleSizePt: clamp(candidate.typography?.titleSizePt, 12, 32, 18),
      h1SizePt: clamp(candidate.typography?.h1SizePt, 9, 24, 14),
      h2SizePt: clamp(candidate.typography?.h2SizePt, 8, 22, 12),
      h3SizePt: clamp(candidate.typography?.h3SizePt, 8, 20, 11),
      captionSizePt: clamp(candidate.typography?.captionSizePt, 8, 16, 10),
      figureCaptionSizePt: clamp(
        candidate.typography?.figureCaptionSizePt,
        8,
        16,
        10,
      ),
      textColor: "#000000",
    },
    paragraph: {
      lineSpacing: clamp(candidate.paragraph?.lineSpacing, 1, 3, 2),
      firstLineIndentMm: clamp(
        candidate.paragraph?.firstLineIndentMm,
        0,
        20,
        0,
      ),
      spaceBeforePt: clamp(candidate.paragraph?.spaceBeforePt, 0, 36, 0),
      spaceAfterPt: clamp(candidate.paragraph?.spaceAfterPt, 0, 36, 0),
      alignment:
        candidate.paragraph?.alignment === "justify" ||
        candidate.paragraph?.alignment === "left"
          ? candidate.paragraph.alignment
          : fallback.paragraph.alignment,
    },
    blockStyles: {
      abstract: normalizeParagraphStyleRule(
        candidate.blockStyles?.abstract,
        fallback.blockStyles.abstract,
      ),
      h1: normalizeParagraphStyleRule(
        candidate.blockStyles?.h1,
        fallback.blockStyles.h1,
      ),
      h2: normalizeParagraphStyleRule(
        candidate.blockStyles?.h2,
        fallback.blockStyles.h2,
      ),
      h3: normalizeParagraphStyleRule(
        candidate.blockStyles?.h3,
        fallback.blockStyles.h3,
      ),
      figureCaption: normalizeParagraphStyleRule(
        candidate.blockStyles?.figureCaption,
        fallback.blockStyles.figureCaption,
      ),
    },
    references: {
      numbering:
        candidate.references?.numbering === "numeric" ||
        candidate.references?.numbering === "preserve"
          ? candidate.references.numbering
          : fallback.references.numbering,
      hangingIndentMm: clamp(
        candidate.references?.hangingIndentMm,
        0,
        20,
        fallback.references.hangingIndentMm,
      ),
      lineSpacing: clamp(
        candidate.references?.lineSpacing,
        1,
        3,
        fallback.references.lineSpacing,
      ),
      spaceAfterPt: clamp(
        candidate.references?.spaceAfterPt,
        0,
        36,
        fallback.references.spaceAfterPt,
      ),
    },
    emphasisRules: normalizeEmphasisRules(candidate.emphasisRules),
    numbering:
      candidate.numbering === "decimal" || candidate.numbering === "none"
        ? candidate.numbering
        : fallback.numbering,
  };
}

function normalizeParagraphStyleRule(
  candidate: Partial<ParagraphStyleRule> | undefined,
  fallback: ParagraphStyleRule,
): ParagraphStyleRule {
  const alignment = ["left", "center", "right", "justify"].includes(
    String(candidate?.alignment),
  )
    ? (candidate?.alignment as ParagraphAlignment)
    : fallback.alignment;
  return {
    lineSpacing: clamp(candidate?.lineSpacing, 1, 3, fallback.lineSpacing),
    firstLineIndentMm: clamp(
      candidate?.firstLineIndentMm,
      0,
      20,
      fallback.firstLineIndentMm,
    ),
    spaceBeforePt: clamp(
      candidate?.spaceBeforePt,
      0,
      36,
      fallback.spaceBeforePt,
    ),
    spaceAfterPt: clamp(
      candidate?.spaceAfterPt,
      0,
      36,
      fallback.spaceAfterPt,
    ),
    alignment,
  };
}

export function parseTemplate(
  value: unknown,
  options: { forceDraft?: boolean } = {},
): ManuscriptTemplate {
  const parsed = templateSchema.parse(value);
  return normalizeTemplate({
    ...parsed,
    status: options.forceDraft ? "draft" : parsed.status,
    verifiedOn: options.forceDraft ? "" : parsed.verifiedOn,
  });
}

export function templateToCssVariables(
  template: ManuscriptTemplate,
): Record<string, string> {
  const normalized = normalizeTemplate(template);
  const letter = normalized.page.size === "Letter";
  return {
    "--body-font": `"${normalized.typography.latinFont}"`,
    "--east-asia-font": `"${normalized.typography.eastAsiaFont}"`,
    "--body-size": `${normalized.typography.bodySizePt}pt`,
    "--line-spacing": String(normalized.paragraph.lineSpacing),
    "--first-line-indent": `${normalized.paragraph.firstLineIndentMm}mm`,
    "--space-before": `${normalized.paragraph.spaceBeforePt}pt`,
    "--space-after": `${normalized.paragraph.spaceAfterPt}pt`,
    "--paragraph-alignment": normalized.paragraph.alignment,
    "--abstract-line-spacing": String(normalized.blockStyles.abstract.lineSpacing),
    "--abstract-first-line-indent": `${normalized.blockStyles.abstract.firstLineIndentMm}mm`,
    "--abstract-space-before": `${normalized.blockStyles.abstract.spaceBeforePt}pt`,
    "--abstract-space-after": `${normalized.blockStyles.abstract.spaceAfterPt}pt`,
    "--abstract-alignment": normalized.blockStyles.abstract.alignment,
    "--h1-line-spacing": String(normalized.blockStyles.h1.lineSpacing),
    "--h1-first-line-indent": `${normalized.blockStyles.h1.firstLineIndentMm}mm`,
    "--h1-space-before": `${normalized.blockStyles.h1.spaceBeforePt}pt`,
    "--h1-space-after": `${normalized.blockStyles.h1.spaceAfterPt}pt`,
    "--h1-alignment": normalized.blockStyles.h1.alignment,
    "--h2-line-spacing": String(normalized.blockStyles.h2.lineSpacing),
    "--h2-first-line-indent": `${normalized.blockStyles.h2.firstLineIndentMm}mm`,
    "--h2-space-before": `${normalized.blockStyles.h2.spaceBeforePt}pt`,
    "--h2-space-after": `${normalized.blockStyles.h2.spaceAfterPt}pt`,
    "--h2-alignment": normalized.blockStyles.h2.alignment,
    "--h3-line-spacing": String(normalized.blockStyles.h3.lineSpacing),
    "--h3-first-line-indent": `${normalized.blockStyles.h3.firstLineIndentMm}mm`,
    "--h3-space-before": `${normalized.blockStyles.h3.spaceBeforePt}pt`,
    "--h3-space-after": `${normalized.blockStyles.h3.spaceAfterPt}pt`,
    "--h3-alignment": normalized.blockStyles.h3.alignment,
    "--caption-size": `${normalized.typography.captionSizePt}pt`,
    "--figure-caption-size": `${normalized.typography.figureCaptionSizePt}pt`,
    "--figure-caption-line-spacing": String(
      normalized.blockStyles.figureCaption.lineSpacing,
    ),
    "--figure-caption-first-line-indent": `${normalized.blockStyles.figureCaption.firstLineIndentMm}mm`,
    "--figure-caption-space-before": `${normalized.blockStyles.figureCaption.spaceBeforePt}pt`,
    "--figure-caption-space-after": `${normalized.blockStyles.figureCaption.spaceAfterPt}pt`,
    "--figure-caption-alignment": normalized.blockStyles.figureCaption.alignment,
    "--reference-hanging-indent": `${normalized.references.hangingIndentMm}mm`,
    "--reference-line-spacing": String(normalized.references.lineSpacing),
    "--reference-space-after": `${normalized.references.spaceAfterPt}pt`,
    "--page-width": letter ? "215.9mm" : "210mm",
    "--page-height": letter ? "279.4mm" : "297mm",
    "--paper-width-px": letter ? "816px" : "793.7px",
    "--margin-top": `${normalized.page.marginsMm.top}mm`,
    "--margin-right": `${normalized.page.marginsMm.right}mm`,
    "--margin-bottom": `${normalized.page.marginsMm.bottom}mm`,
    "--margin-left": `${normalized.page.marginsMm.left}mm`,
    "--title-size": `${normalized.typography.titleSizePt}pt`,
    "--h1-size": `${normalized.typography.h1SizePt}pt`,
    "--h2-size": `${normalized.typography.h2SizePt}pt`,
    "--h3-size": `${normalized.typography.h3SizePt}pt`,
  };
}
